import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Phone } from "lucide-react";
import { BRAND, SHOWCASE } from "@/config/brand";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";

// Honest "how we work" description block. Sits directly under the TV/press-reel
// carousel on both the marketing landing and the About page. Uses Kit's own
// phrasing — no boastful awards, no agency sizzle.
export default function HowWeWork() {
  return (
    <section
      className="relative z-10 py-16 md:py-24"
      data-testid="how-we-work"
      style={{ background: "transparent" }}
    >
      <div className="mx-auto max-w-[980px] px-6 md:px-10">
        <div
          className="relative rounded-3xl p-6 md:p-10 border overflow-hidden"
          style={{
            background: "rgba(11,21,36,0.72)",
            borderColor: `${teal}44`,
            backdropFilter: "blur(10px)",
            boxShadow: `0 40px 120px -50px ${teal}55`,
          }}
        >
          {/* Kicker */}
          <div className="text-[10px] uppercase tracking-[0.24em] font-black mb-3" style={{ color: teal }}>
            How we actually work
          </div>

          <h3
            className="font-black tracking-[-0.02em] leading-[1.05]"
            style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "white" }}
          >
            SEO is a hobby for Kit — so it never becomes a{" "}
            <span
              style={{
                backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              padded line-item on your invoice
            </span>
            .
          </h3>

          <div className="mt-6 space-y-4 text-base md:text-[17px] leading-relaxed text-white/85">
            <p>
              You&apos;ll learn that pretty quickly when Kit quotes you a real, honest figure to get your website
              ranked — instead of trying to <strong className="text-white">hold your site hostage</strong> after launch. He&apos;ll also send
              you links to sites we&apos;ve personally built this year, so you can see the work with your own eyes.
            </p>
            <p>
              Tell us what you&apos;ve got going on and we&apos;ll tell you <strong className="text-white">exactly</strong> what
              we&apos;re going to do to make you rank. Every website comes with{" "}
              <strong style={{ color: gold }}>100 pages of exclusive content</strong> — show us one other outfit
              that does this without charging you $3,500 and up.
            </p>
            <p>
              Not us. Our average website-design projects range from{" "}
              <strong className="text-white">$600 – $2,000</strong>, and we have{" "}
              <strong className="text-white">payment options</strong> if that&apos;s what makes it doable.
            </p>
          </div>

          {/* Bullet strip */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "In-house team of 4 — no overseas subcontracting",
              "Real quotes, real links, real work you can inspect",
              "100 pages of exclusive content on every build",
              "$600 – $2,000 typical range · payment options available",
            ].map((line) => (
              <div
                key={line}
                className="flex items-start gap-2.5 rounded-xl p-3 border"
                style={{ borderColor: `${teal}33`, background: "rgba(20,184,166,0.05)" }}
              >
                <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: teal }} />
                <span className="text-sm text-white/90 leading-snug">{line}</span>
              </div>
            ))}
          </div>

          {/* CTA row */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="tel:+12148155966"
              className="inline-flex items-center gap-2 px-5 h-12 rounded-full font-semibold text-sm"
              style={{
                background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
                color: "white",
                boxShadow: `0 18px 40px -18px ${teal}aa`,
              }}
              data-testid="how-we-work-cta-call"
            >
              <Phone size={14} /> Get a real quote — (214) 815-5966 <ArrowRight size={14} />
            </a>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-5 h-12 rounded-full font-semibold text-sm border"
              style={{ borderColor: `${teal}66`, color: "white" }}
              data-testid="how-we-work-cta-about"
            >
              Meet the team of 4
            </Link>
          </div>
        </div>

        {/* Small footnote grid — captions for the images above */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 text-[12px] leading-relaxed" style={{ color: C.textFade }}>
          <div className="rounded-xl p-3 border" style={{ borderColor: C.line, background: "rgba(255,255,255,0.02)" }}>
            <div className="font-black uppercase tracking-[0.2em] text-[10px] mb-1" style={{ color: teal }}>
              About the studio image
            </div>
            A studio-style concept piece we mocked up while press outreach is in progress. Kit&apos;s words, but the network chyron is a placeholder — we haven&apos;t been on air anywhere yet.
          </div>
          <div className="rounded-xl p-3 border" style={{ borderColor: C.line, background: "rgba(255,255,255,0.02)" }}>
            <div className="font-black uppercase tracking-[0.2em] text-[10px] mb-1" style={{ color: teal }}>
              About the client screenshots
            </div>
            Every screenshot in the reel is a real live site we personally built this year. Tap a slide and you can visit the site yourself: {SHOWCASE.map((s) => s.name).join(" · ")}.
          </div>
        </div>
      </div>
    </section>
  );
}
